import { isPlatformBrowser } from '@angular/common';
import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
} from '@angular/core';
import supportsWebP from 'supports-webp';

@Component({
  selector: 'app-img-format',
  templateUrl: './img-format.component.html',
  styleUrls: ['./img-format.component.scss'],
})
export class ImgFormatComponent implements AfterContentChecked {
  @Input() src: string;
  @Input() alt: string;
  @Input() width: number = 0;
  @Input() height: number = 0;
  @Input() showBackground: boolean = true;
  @Input() disableLazyLoad: boolean = false;
  @Input() aspectRatio: string = '';

  @ViewChild('imageWrapper', { static: false }) imageWrapper: ElementRef;

  isBrowser: boolean = false;
  imgUrl: string = '';
  wrapperWidth: number = 0;
  computedHeight: number = 0;
  webPSupported: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) platformId: string,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    supportsWebP.then((supported) => {
      this.webPSupported = supported;
      this.cdr.markForCheck();
    });
  }

  ngOnInit(): void {}

  ngAfterContentChecked(): void {
    this.setImgUrl();

    this.calculateDimensions();

    this.cdr.detectChanges();
  }

  setImgUrl(): void {
    if (!this.src || this.wrapperWidth === 0)
      return;

    // Build up the variable first before setting it (avoids unnecessary network requests)
    let imageUrl = typeof window !== 'undefined' ? this.src : '';

    const pixelRatio =
      typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

    let scaledWidth = Math.round(this.wrapperWidth * pixelRatio);
    let scaledHeight = Math.round(this.computedHeight * pixelRatio);
    
    // Stop oversized images
    if (scaledWidth > 2560) {
      scaledWidth = 2560;

      const adjustedComputedHeight = this.setComputedHeight(
        2560,
        this.aspectRatio
      );

      scaledHeight = Math.round(adjustedComputedHeight * pixelRatio);
    }

    if(!isNaN(scaledWidth) && !isNaN(scaledHeight) && scaledHeight > 0) {
      imageUrl = `${this.src}?w=${scaledWidth}&h=${scaledHeight}`;
    } else if(!isNaN(scaledWidth)) {
      imageUrl = `${this.src}?w=${scaledWidth}`;
    }

    if (this.webPSupported) {
      imageUrl += '&webp';
    }

    if (!this.disableLazyLoad) {
      this.renderer.setAttribute(
        this.imageWrapper.nativeElement,
        'loading',
        'lazy'
      );
    }

    // Update the image URL once it's ready
    this.imgUrl = imageUrl;
  }

  calculateDimensions(): void {
    if (this.imageWrapper?.nativeElement) {
      this.wrapperWidth = this.imageWrapper.nativeElement.offsetWidth;
      this.computedHeight = this.setComputedHeight(
        this.wrapperWidth,
        this.aspectRatio
      );
    }
    this.setDimensions();
  }

  setComputedHeight(width: number, aspectRatio: string): number {
    const [aspectWidth, aspectHeight] = aspectRatio && aspectRatio !== "OTHER"
      ? aspectRatio.split(':').map(Number)
      : [0, 0];

    if (!aspectWidth || !aspectHeight) {
      return 0;
    }

    return (width / aspectWidth) * aspectHeight;
  }

  setDimensions(): void {
    if (this.wrapperWidth > 0) {
      const imgElement = this.imageWrapper.nativeElement;
      const scaledWidth = Math.round(this.wrapperWidth);
      this.renderer.setAttribute(imgElement, 'width', `${scaledWidth}`);
      
      if (this.computedHeight > 0) {
        const scaledHeight = Math.round(this.computedHeight);
        this.renderer.setAttribute(imgElement, 'height', `${scaledHeight}`);
      }
    }
  }
}
